import { useCustom, useTranslate } from '@refinedev/core';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import {
  ENTRY_LIMIT,
  HISTORY_FIELDS,
  OPENS_FIELDS,
  PLAN_FIELDS,
  SECTION_IDS,
  SECTION_KIND,
  STATUS_FIELDS,
  blankRow,
  draftFromEntries,
  entriesFromDraft,
  entryLine,
  type DraftRow,
  type HistoryRow,
  type OpensRow,
  type PlanRow,
  type SectionId,
  type StatusRow,
} from './model';
import { useSafetyPlanCommand } from './useSafetyPlanCommand';

/**
 * E38 · SafetyPlan (portal) + UpdateSafetyPlanSection (in session) + ReviewSafetyPlan + SafetyPlanHistory +
 * SafetyPlanOpensByWeek — "Client Hub — Safety plan": the same plan the client has on the phone, laid out as the clinician
 * walks it with the client in session (the Safety Planning Intervention is a collaborative, in-session act).
 *
 *   · visible through a relation with VIEW permission, editable only with EDIT permission (E07); with view alone it is
 *     read-only and says so;
 *   · "Edit with the client" saves one section as the therapist (the client's phone shows it at once, marked as the
 *     therapist's); "Mark reviewed" records who and when with an optional next-review date and note;
 *   · History lists every change with its author and the reviews, newest first (the log is the audit);
 *   · the weekly opens are a count and nothing else — not when, not what was read; the client is told you can see it.
 * NOTHING AUTOMATIC: no alert, risk score or notification is derived from the plan or from its opens.
 */
type Props = {
  clientId: string;
  clientName: string;
  locale: 'en' | 'ru';
};

const timeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

export const ClientSafetyPlanTab = ({ clientId, locale }: Props) => {
  const t = useTranslate();
  const dateLocale = locale === 'ru' ? ru : enUS;
  const { run, isLoading } = useSafetyPlanCommand();
  const [editing, setEditing] = useState<SectionId | null>(null);
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [reviewing, setReviewing] = useState(false);
  const [nextReviewBy, setNextReviewBy] = useState('');
  const [note, setNote] = useState('');
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  const subject = { name: 'subject', type: 'ID', value: clientId };
  const plan = useCustom<PlanRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'safetyPlan',
      fields: PLAN_FIELDS,
      variables: { subject },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const status = useCustom<StatusRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'safetyPlanStatus',
      fields: STATUS_FIELDS,
      variables: { subject },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const history = useCustom<HistoryRow[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'safetyPlanHistory',
      fields: HISTORY_FIELDS,
      variables: { subject: { name: 'subject', type: 'ID!', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const opens = useCustom<OpensRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'safetyPlanOpensByWeek',
      fields: OPENS_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
        timeZone: { name: 'timeZone', type: 'String', value: timeZone() },
      },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });

  const view = plan.data?.data;
  const st = status.data?.data;
  const hist = useMemo(() => history.data?.data ?? [], [history.data]);
  const opensView = opens.data?.data;
  const mayEdit = view?.mayEdit === true;
  const sectionOf = (id: SectionId) => view?.sections.find((s) => s.section === id);

  const refetchAll = () => {
    plan.refetch();
    status.refetch();
    history.refetch();
  };

  const day = (iso: string) =>
    format(parseISO(iso), 'd MMM yyyy', { locale: dateLocale });
  const dateTime = (iso: string) =>
    format(parseISO(iso), 'd MMM yyyy, HH:mm', { locale: dateLocale });

  const openEdit = (id: SectionId) => {
    setModalError(null);
    setMessage(null);
    setRows(draftFromEntries(id, sectionOf(id)?.entries ?? []));
    setEditing(id);
  };

  const patch = (key: string, change: Partial<DraftRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...change } : r)));

  const onSaveSection = async () => {
    if (!editing) return;
    const res = await run(
      'updateSafetyPlanSectionInSession',
      { subject: clientId, section: editing, entries: entriesFromDraft(editing, rows) },
      t('safetyPlan.hub.edit.error'),
    );
    if (res.ok) {
      setEditing(null);
      setMessage({ type: 'success', text: t('safetyPlan.hub.edit.saved') });
      refetchAll();
    } else {
      setModalError(res.message);
    }
  };

  const onReview = async () => {
    const res = await run(
      'reviewSafetyPlan',
      {
        subject: clientId,
        ...(nextReviewBy ? { nextReviewBy } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      },
      t('safetyPlan.hub.review.error'),
    );
    if (res.ok) {
      setReviewing(false);
      setNextReviewBy('');
      setNote('');
      setMessage({ type: 'success', text: t('safetyPlan.hub.review.saved') });
      refetchAll();
    } else {
      setModalError(res.message);
    }
  };

  const whoLabel = (r: { byClient: boolean; performerName: string | null }) =>
    r.byClient
      ? t('safetyPlan.hub.byClient')
      : r.performerName || t('safetyPlan.yourTherapist');

  const kind = editing ? SECTION_KIND[editing] : 'text';

  const historyColumns = [
    {
      title: t('safetyPlan.history.when'),
      width: 170,
      render: (_: unknown, r: HistoryRow) => dateTime(r.occurredAt),
    },
    {
      title: t('safetyPlan.history.what'),
      render: (_: unknown, r: HistoryRow) =>
        r.kind === 'review' ? (
          <Space direction="vertical" size={0}>
            <span>{t('safetyPlan.history.reviewedBy', { who: whoLabel(r) })}</span>
            {r.nextReviewBy ? (
              <span>
                {t('safetyPlan.history.nextReview', { date: day(r.nextReviewBy) })}
              </span>
            ) : null}
            {r.note ? <span>{r.note}</span> : null}
          </Space>
        ) : (
          <Space direction="vertical" size={0}>
            <span>
              {t('safetyPlan.history.savedPart', {
                who: whoLabel(r),
                section: r.section ? t(`safetyPlan.sections.${r.section}.title`) : '',
              })}
            </span>
            <span>
              {(r.entries ?? []).length
                ? (r.entries ?? []).map(entryLine).join(' · ')
                : t('safetyPlan.history.noEntries')}
            </span>
          </Space>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }} data-testid="safetyplan-hub">
      <Typography.Title level={4} style={{ margin: 0 }}>
        {t('safetyPlan.hub.title')}
      </Typography.Title>

      {plan.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('safetyPlan.history.loadError')}
          data-testid="safetyplan-hub-load-error"
        />
      ) : null}
      {message ? (
        <Alert
          type={message.type}
          showIcon
          message={message.text}
          closable
          onClose={() => setMessage(null)}
          data-testid={`safetyplan-hub-message-${message.type}`}
        />
      ) : null}
      {view && !mayEdit ? (
        <Alert type="info" showIcon message={t('safetyPlan.hub.readOnly')} data-testid="safetyplan-hub-read-only" />
      ) : null}
      {view && !view.hasPlan ? (
        <Alert type="info" showIcon message={t('safetyPlan.hub.noPlan')} data-testid="safetyplan-hub-no-plan" />
      ) : null}

      <Card size="small">
        <Space direction="vertical" size={4}>
          <span data-testid="safetyplan-hub-reviewed">
            {st?.reviewedAt
              ? st.reviewedWithName
                ? t('safetyPlan.hub.lastReviewed', { date: day(st.reviewedAt), name: st.reviewedWithName })
                : t('safetyPlan.hub.lastReviewedNoName', { date: day(st.reviewedAt) })
              : t('safetyPlan.hub.neverReviewed')}
          </span>
          {st?.nextReviewBy ? (
            <span data-testid="safetyplan-hub-next-review">
              {t('safetyPlan.hub.nextReview', { date: day(st.nextReviewBy) })}
            </span>
          ) : null}
          {opensView ? (
            <span data-testid="safetyplan-hub-opens">
              {t('safetyPlan.hub.opensThisWeek', { count: opensView.thisWeek })}
            </span>
          ) : null}
          <Typography.Text type="secondary">{t('safetyPlan.hub.opensNote')}</Typography.Text>
          {opensView ? (
            <Typography.Text type="secondary" data-testid="safetyplan-hub-opens-weeks">
              {t('safetyPlan.hub.opensWeeks')}:{' '}
              {opensView.weeks
                .slice(0, 4)
                .map((w) => `${t('safetyPlan.hub.weekOf', { date: day(w.weekStart) })} — ${w.count}`)
                .join(' · ')}
            </Typography.Text>
          ) : null}
          {mayEdit && view?.hasPlan ? (
            <div>
              <Button
                data-testid="safetyplan-hub-review-open"
                onClick={() => {
                  setModalError(null);
                  setMessage(null);
                  setReviewing(true);
                }}
              >
                {t('safetyPlan.hub.review.action')}
              </Button>
            </div>
          ) : null}
        </Space>
      </Card>

      {SECTION_IDS.map((id) => {
        const s = sectionOf(id);
        const entries = s?.entries ?? [];
        return (
          <Card
            key={id}
            size="small"
            title={t(`safetyPlan.sections.${id}.title`)}
            data-testid={`safetyplan-hub-section-${id}`}
            extra={
              mayEdit ? (
                <Button size="small" onClick={() => openEdit(id)} data-testid={`safetyplan-hub-edit-${id}`}>
                  {t('safetyPlan.hub.edit.action')}
                </Button>
              ) : null
            }
          >
            {entries.length ? (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {entries.map((e, i) => (
                  <li key={i} data-testid={`safetyplan-hub-entry-${id}-${i}`}>
                    {entryLine(e)}
                  </li>
                ))}
              </ul>
            ) : (
              <Typography.Text type="secondary">{t('safetyPlan.empty')}</Typography.Text>
            )}
            {entries.length && s && !s.byClient ? (
              <Tag color="blue" style={{ marginTop: 8 }} data-testid={`safetyplan-hub-section-${id}-mark`}>
                {s.performerName
                  ? t('safetyPlan.addedByName', { name: s.performerName })
                  : t('safetyPlan.addedByTherapist')}
              </Tag>
            ) : null}
          </Card>
        );
      })}

      <Card size="small" title={t('safetyPlan.history.title')} data-testid="safetyplan-hub-history">
        <Table<HistoryRow>
          columns={historyColumns}
          dataSource={hist}
          loading={history.isFetching}
          locale={{ emptyText: t('safetyPlan.history.empty') }}
          onRow={(r) => ({ 'data-testid': `safetyplan-hub-history-row-${r.entryId}` }) as never}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          rowKey="entryId"
          size="small"
        />
      </Card>

      <Modal
        open={editing !== null}
        title={editing ? `${t('safetyPlan.hub.edit.title')} — ${t(`safetyPlan.sections.${editing}.title`)}` : ''}
        onCancel={() => setEditing(null)}
        footer={[
          <Button key="cancel" onClick={() => setEditing(null)} data-testid="safetyplan-hub-modal-cancel">
            {t('safetyPlan.hub.edit.cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={isLoading}
            onClick={onSaveSection}
            data-testid="safetyplan-hub-modal-save"
          >
            {t('safetyPlan.hub.edit.save')}
          </Button>,
        ]}
        destroyOnClose
      >
        {editing ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text type="secondary">{t(`safetyPlan.sections.${editing}.prompt`)}</Typography.Text>
            {modalError ? (
              <Alert type="error" showIcon message={modalError} data-testid="safetyplan-hub-modal-error" />
            ) : null}
            {rows.map((r, i) => (
              <Space key={r.key} align="start" wrap>
                {kind === 'text' ? (
                  <Input.TextArea
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    data-testid={`safetyplan-hub-modal-text-${i}`}
                    maxLength={500}
                    onChange={(e) => patch(r.key, { text: e.target.value })}
                    placeholder={t('safetyPlan.hub.edit.entry')}
                    style={{ width: 360 }}
                    value={r.text}
                  />
                ) : (
                  <>
                    <Input
                      data-testid={`safetyplan-hub-modal-name-${i}`}
                      maxLength={500}
                      onChange={(e) => patch(r.key, { name: e.target.value })}
                      placeholder={t('safetyPlan.hub.edit.name')}
                      style={{ width: 200 }}
                      value={r.name}
                    />
                    <Input
                      data-testid={`safetyplan-hub-modal-phone-${i}`}
                      maxLength={64}
                      onChange={(e) => patch(r.key, { phone: e.target.value })}
                      placeholder={t('safetyPlan.hub.edit.phone')}
                      style={{ width: 160 }}
                      value={r.phone}
                    />
                    {kind === 'placeOrPerson' ? (
                      <Radio.Group
                        onChange={(e) => patch(r.key, { kind: e.target.value })}
                        value={r.kind}
                      >
                        <Radio.Button value="person">{t('safetyPlan.hub.edit.person')}</Radio.Button>
                        <Radio.Button value="place">{t('safetyPlan.hub.edit.place')}</Radio.Button>
                      </Radio.Group>
                    ) : null}
                  </>
                )}
                {rows.length > 1 ? (
                  <Button
                    danger
                    size="small"
                    data-testid={`safetyplan-hub-modal-remove-${i}`}
                    onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
                  >
                    {t('safetyPlan.hub.edit.remove')}
                  </Button>
                ) : null}
              </Space>
            ))}
            {rows.length < ENTRY_LIMIT ? (
              <Button
                data-testid="safetyplan-hub-modal-add"
                onClick={() => setRows((rs) => [...rs, blankRow()])}
              >
                {t('safetyPlan.hub.edit.add')}
              </Button>
            ) : (
              <Typography.Text type="secondary">{t('safetyPlan.edit.maxEntries')}</Typography.Text>
            )}
          </Space>
        ) : null}
      </Modal>

      <Modal
        open={reviewing}
        title={t('safetyPlan.hub.review.title')}
        onCancel={() => setReviewing(false)}
        footer={[
          <Button key="cancel" onClick={() => setReviewing(false)} data-testid="safetyplan-hub-review-cancel">
            {t('safetyPlan.hub.review.cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={isLoading}
            onClick={onReview}
            data-testid="safetyplan-hub-review-save"
          >
            {t('safetyPlan.hub.review.save')}
          </Button>,
        ]}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {modalError ? (
            <Alert type="error" showIcon message={modalError} data-testid="safetyplan-hub-review-error" />
          ) : null}
          <label>
            {t('safetyPlan.hub.review.nextReviewBy')}
            <Input
              data-testid="safetyplan-hub-review-next"
              onChange={(e) => setNextReviewBy(e.target.value)}
              type="date"
              value={nextReviewBy}
            />
          </label>
          <label>
            {t('safetyPlan.hub.review.note')}
            <Input.TextArea
              autoSize={{ minRows: 2, maxRows: 5 }}
              data-testid="safetyplan-hub-review-note"
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('safetyPlan.hub.review.notePlaceholder')}
              value={note}
            />
          </label>
        </Space>
      </Modal>
    </Space>
  );
};
